import React, { useEffect, useState } from "react";
import { Empty, Select, Space, Tabs, Typography } from "antd";
import { useApp, AppState } from "../../contexts/AppContext";
import { entityApi } from "../../services/entityApi";
import { RaidEntity } from "../../types";
import { entityCategory, isBoss } from "../../utils/raidAuthoring";
import BossBasicInfo from "../tabs/BossBasicInfo";
import SkillsTab from "../tabs/SkillsTab";
import EffectsTab from "../tabs/EffectsTab";
import TriggersTabV3 from "../tabs/TriggersTabV3";
import RelationsTab from "../tabs/RelationsTab";
import MotionEffectsTab from "../tabs/MotionEffectsTab";
import RaidWorkspace from "../tabs/RaidWorkspace";
import DropItemManagementTab from "../tabs/DropItemManagementTab";
const ContentArea: React.FC = () => {
  const { state, setActiveTab, selectEntity, selectBoss } = useApp();
  const [entities, setEntities] = useState<RaidEntity[]>([]);
  useEffect(() => {
    let current = true;
    entityApi
      .getAll()
      .then((rows) => {
        if (current) setEntities(rows);
      })
      .catch(() => {
        if (current) setEntities([]);
      });
    return () => {
      current = false;
    };
  }, [state.selectedBoss, state.selectedEntity]);
  if (state.viewingCommon)
    return (
      <Tabs
        activeKey={state.commonResourceType || "skills"}
        onChange={(key) => setActiveTab(key as "skills" | "effects")}
        tabBarStyle={{ paddingLeft: 24 }}
        items={[
          {
            key: "skills",
            label: "공통 스킬",
            children: <SkillsTab isCommonMode />,
          },
          {
            key: "effects",
            label: "공통 효과",
            children: <EffectsTab isCommonMode />,
          },
        ]}
      />
    );
  if (!state.selectedBoss && !state.selectedEntity) {
    if (state.activeTab === "raid-management") return <RaidWorkspace />;
    if (state.activeTab === "drop-item-management")
      return <DropItemManagementTab />;
    return (
      <div className="resource-page">
        <Empty description="보스나 몬스터를 선택해주세요" />
      </div>
    );
  }
  const code =
    state.selectedEntity?.entity_code || state.selectedBoss?.boss_code;
  const rootCode =
    state.selectedEntity?.parent_code || state.selectedBoss?.boss_code || code;
  const family = entities.filter(
    (e) =>
      e.entity_code === rootCode ||
      e.parent_code === rootCode ||
      e.entity_code === code,
  );
  const choose = (value: string) => {
    const entity = entities.find((e) => e.entity_code === value);
    if (!entity) return;
    if (isBoss(entity))
      selectBoss({
        id: entity.id || 0,
        boss_code: entity.entity_code,
        boss_name: entity.entity_name,
        description: entity.description || "",
        max_hp: entity.max_hp,
        phase_count: entity.phase_count,
        difficulty: entity.difficulty,
        game_version: entity.game_version,
        created_at: entity.created_at,
        updated_at: entity.updated_at,
      });
    else selectEntity(entity);
  };
  return (
    <div>
      {family.length > 1 && (
        <Space
          wrap
          style={{
            padding: "14px 24px",
            borderBottom: "1px solid #303030",
            width: "100%",
          }}
        >
          <Typography.Text type="secondary">관련 몬스터</Typography.Text>
          <Select
            aria-label="관련 몬스터 이동"
            value={code}
            onChange={choose}
            showSearch
            optionFilterProp="label"
            style={{ width: 300, maxWidth: "100%" }}
            options={family.map((e) => ({
              value: e.entity_code,
              label: `${e.entity_name} · ${entityCategory(e)}`,
            }))}
          />
        </Space>
      )}
      <Tabs
        activeKey={state.activeTab}
        onChange={(key) => setActiveTab(key as AppState["activeTab"])}
        tabBarStyle={{ paddingLeft: 24 }}
        items={[
          {
            key: "basic",
            label: "기본 정보",
            children: (
              <BossBasicInfo
                key={code}
                bossCode={code}
                entityMode={!!state.selectedEntity}
              />
            ),
          },
          {
            key: "skills",
            label: "스킬",
            children: (
              <SkillsTab
                key={code}
                bossCode={state.selectedBoss?.boss_code}
                entityCode={state.selectedEntity?.entity_code}
              />
            ),
          },
          {
            key: "effects",
            label: "효과",
            children: (
              <EffectsTab
                key={code}
                bossCode={state.selectedBoss?.boss_code}
                entityCode={state.selectedEntity?.entity_code}
              />
            ),
          },
          {
            key: "triggers-v3",
            label: "트리거",
            children: (
              <TriggersTabV3
                key={code}
                bossCode={state.selectedBoss?.boss_code}
                entityCode={state.selectedEntity?.entity_code}
              />
            ),
          },
          {
            key: "relations",
            label: "연결 관계",
            children: (
              <RelationsTab
                key={code}
                bossCode={state.selectedBoss?.boss_code}
                entityCode={state.selectedEntity?.entity_code}
              />
            ),
          },
          {
            key: "motion-effects",
            label: "모션 및 효과",
            children: (
              <MotionEffectsTab
                key={code}
                bossCode={state.selectedBoss?.boss_code}
                entityCode={state.selectedEntity?.entity_code}
              />
            ),
          },
        ]}
      />
    </div>
  );
};
export default ContentArea;
